/**
 * Estado del proceso de renovación de las cookies de SSTA (sistema de turnos
 * "STOP" de ssta.gob.ar), que mantiene el servicio agrology-stop-cookie-refresher.
 *
 * Contrato con el backend — dos reglas que evitan lógica de fechas en el front:
 *
 *  1. El backend manda los textos `*Human` ya redactados en español ("vence en
 *     4 horas", "hace 12 minutos"). El repo no tiene ningún pipe de fecha
 *     relativa, y este no es el lugar para estrenar uno.
 *  2. Las fechas absolutas vienen en ISO-8601 UTC y se pintan con el DatePipe
 *     nativo (`date:'dd/MM/yyyy HH:mm'`).
 *
 * El VALOR de la cookie nunca viaja: es un token de sesión vivo contra un sitio
 * de gobierno. Solo llega `fingerprint`, un hash corto que sirve para comparar
 * si api2 está usando la misma cookie que escribió el refresher.
 */

/** Estados que acepta el componente compartido `status-indicator`. */
export type StopCookieStatus = 'success' | 'warning' | 'error';

export class StopCookieKey {
  /** Nombre de la cookie tal como la conoce SSTA: "STOPv5-prd_REFRESH". */
  name: string;
  /** Key de Redis donde vive: "ssta:refresh_cookie". */
  redisKey: string;
  /** Etiqueta para la UI: "Cookie de sesión larga". */
  label: string;

  status: StopCookieStatus;
  loadedAt: string;
  loadedAtHuman: string;
  expiresAt: string;
  expiresAtHuman: string;
  expiresInMinutes: number;

  /** Hash corto (sha256[:8]). NUNCA el valor de la cookie. */
  fingerprint: string;

  /**
   * true para la cookie corta (STOPv5-prd). Vive ~5 minutos pero solo se
   * reescribe cuando se renueva la larga, o sea cada ~24h: figura vencida el
   * ~99.7% del tiempo POR DISEÑO, y api2 la renueva bajo demanda con
   * /login/refresh.
   *
   * Sin esta bandera la solapa mostraría una alerta permanente sobre algo que
   * funciona bien. El componente la usa para pintar un ícono informativo con
   * tooltip en vez del semáforo.
   */
  expectedStale: boolean;
}

export class StopCronInfo {
  /** false en dev/demo: el schedule de EventBridge solo se crea en prod. */
  enabled: boolean;
  /** Cadencia del chequeo: "rate(5 minutes)". No es la de renovación. */
  schedule: string;
  /**
   * Próximo chequeo del cron. Ojo: la mayoría son no-ops baratos; el cron solo
   * renueva cuando faltan menos de `renewMarginMinutes` para el vencimiento.
   */
  nextPollAt: string;
  nextPollAtHuman: string;
  /** Próxima renovación REAL estimada (≈ vencimiento − margen). */
  nextRenewalAt: string;
  nextRenewalAtHuman: string;
  renewMarginMinutes: number;
}

export class StopLastRun {
  ranAt: string;
  ranAtHuman: string;
  /** 'renewed' | 'noop' | 'error' */
  outcome: string;
  errorMessage: string;
  traceId: string;
}

export class StopRefreshJob {
  jobId: string;
  /** 'running' | 'completed' | 'failed' */
  status: string;
  startedAt: string;
  finishedAt: string;
  errorMessage: string;
  /** Quién disparó la renovación manual (auditoría). */
  triggeredBy: string;
}

/**
 * Tope de renovaciones manuales. Es GLOBAL sobre el recurso, no por company:
 * la sesión de SSTA es una sola para toda la plataforma (login público, sin
 * credenciales por empresa), así que un tope por company no acotaría nada.
 */
export class StopRateLimit {
  limit: number;
  used: number;
  remaining: number;
  resetsAt: string;
  resetsAtHuman: string;
  allowed: boolean;
}

/**
 * Qué fuente de cookie está usando api2 REALMENTE.
 *
 * Es el dato más importante de la pantalla: sin esto, la solapa mostraría el
 * estado del ESCRITOR (el refresher, que puede estar perfecto) mientras api2
 * sirve una cookie vieja del Google Sheet. `diverged` en true pinta un banner
 * de advertencia.
 */
export class StopConsumption {
  /** 'REFRESHER' | 'GOOGLE_SHEET' | 'UNKNOWN' */
  source: string;
  diverged: boolean;
  checkedAt: string;
}

export class StopNotifiedUser {
  userId: number;
  accountId: number;
  email: string;
  firstName: string;
  lastName: string;
  language: string;
}

export class StopCookiesStatus {
  cookies: StopCookieKey[];
  cron: StopCronInfo;
  lastRun: StopLastRun;
  refreshJob: StopRefreshJob;
  rateLimit: StopRateLimit;
  consumption: StopConsumption;
  /** Advertencia del backend, p. ej. si la lista de notificados está vacía. */
  warnings: string[];
}

/**
 * Respuesta del POST /refresh. NO es un StopCookiesStatus: el backend devuelve
 * solo el desenlace del disparo, sin el estado completo de las cookies.
 *
 * `outcome`:
 *  - 'accepted'      (202) el trabajo arrancó; hay que polear el GET /status.
 *  - 'already_valid' (200) la cookie seguía vigente: no se hizo login ni se
 *                    consumió cuota. Es el camino MÁS frecuente, por diseño —
 *                    el objetivo es sostener un solo login por día.
 */
export class StopRefreshResult {
  outcome: string;
  message: string;
  jobId: string;
  expiresInMinutes: number;
  rateLimit: StopRateLimit;
}
